import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { seedProductions } from '../data/productions';
import { useAuth } from './AuthContext';
import { isLive, isUpcoming, slugify } from '../utils/production';
import type { Production, ProductionInput, PublicationStatus } from '../types/production';
interface CatalogContextValue {
  productions: Production[];
  published: Production[];
  upcoming: Production[];
  featured: Production | null;
  hasDemoContent: boolean;
  getBySlug: (slug: string) => Production | undefined;
  getById: (id: string) => Production | undefined;
  createProduction: (input: ProductionInput) => Promise<Production>;
  updateProduction: (id: string, input: ProductionInput) => Promise<Production>;
  setProductionStatus: (id: string, status: PublicationStatus) => Promise<void>;
  deleteProduction: (id: string) => Promise<void>;
}
const STORAGE_KEY = 'nicole.catalog.v1';
const CatalogContext = createContext<CatalogContextValue | null>(null);
function loadCatalog(): Production[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Production[];
  } catch {
    /* fall back to seed */
  }
  return seedProductions;
}
function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
function uniqueSlug(title: string, existing: Production[]): string {
  const base = slugify(title) || 'produccion';
  let slug = base;
  let n = 2;
  while (existing.some(p => p.slug === slug)) slug = `${base}-${n++}`;
  return slug;
}
function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `p-${Date.now().toString(36)}`;
}

/**
 * Local catalog adapter. Swap the write methods for calls to your API once a backend
 * and database are connected. The session check here is client-side only; the server
 * must authorize every write.
 */
export function CatalogProvider({
  children,
  includeDemo
}: {
  children: React.ReactNode;
  includeDemo: boolean;
}) {
  const {
    session
  } = useAuth();
  const [all, setAll] = useState<Production[]>(loadCatalog);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    } catch {
      /* ignore quota errors */
    }
  }, [all]);
  const productions = useMemo(() => includeDemo ? all : all.filter(p => !p.isDemo), [all, includeDemo]);
  const published = useMemo(() => productions.filter(p => isLive(p)).sort((a, b) => b.createdAt.localeCompare(a.createdAt)), [productions]);
  const upcoming = useMemo(() => productions.filter(p => isUpcoming(p)).sort((a, b) => (a.releaseDate ?? '').localeCompare(b.releaseDate ?? '')), [productions]);
  const featured = useMemo(() => published.find(p => p.isFeatured) ?? published[0] ?? null, [published]);
  const assertAuthorized = useCallback(() => {
    if (!session) throw new Error('Tu sesión ha caducado. Inicia sesión para continuar.');
  }, [session]);
  const createProduction = useCallback(async (input: ProductionInput) => {
    assertAuthorized();
    await wait(500);
    const created: Production = {
      ...input,
      id: newId(),
      slug: uniqueSlug(input.title, all),
      isDemo: false,
      createdAt: new Date().toISOString()
    };
    setAll(prev => [created, ...prev.map(p => created.isFeatured ? {
      ...p,
      isFeatured: false
    } : p)]);
    return created;
  }, [all, assertAuthorized]);
  const updateProduction = useCallback(async (id: string, input: ProductionInput) => {
    assertAuthorized();
    await wait(500);
    const current = all.find(p => p.id === id);
    if (!current) throw new Error('La publicación ya no existe.');
    const updated: Production = {
      ...current,
      ...input
    };
    setAll(prev => prev.map(p => {
      if (p.id === id) return updated;
      return updated.isFeatured ? {
        ...p,
        isFeatured: false
      } : p;
    }));
    return updated;
  }, [all, assertAuthorized]);
  const setProductionStatus = useCallback(async (id: string, status: PublicationStatus) => {
    assertAuthorized();
    await wait(350);
    setAll(prev => prev.map(p => p.id === id ? {
      ...p,
      status,
      releaseDate: status === 'scheduled' ? p.releaseDate : null
    } : p));
  }, [assertAuthorized]);
  const deleteProduction = useCallback(async (id: string) => {
    assertAuthorized();
    await wait(400);
    setAll(prev => prev.filter(p => p.id !== id));
  }, [assertAuthorized]);
  const getBySlug = useCallback((slug: string) => productions.find(p => p.slug === slug), [productions]);
  const getById = useCallback((id: string) => productions.find(p => p.id === id), [productions]);
  const value = useMemo<CatalogContextValue>(() => ({
    productions,
    published,
    upcoming,
    featured,
    hasDemoContent: productions.some(p => p.isDemo),
    getBySlug,
    getById,
    createProduction,
    updateProduction,
    setProductionStatus,
    deleteProduction
  }), [productions, published, upcoming, featured, getBySlug, getById, createProduction, updateProduction, setProductionStatus, deleteProduction]);
  return <CatalogContext.Provider value={value} data-id="element-291" data-mp="3vovtqh0tr5xj">{children}</CatalogContext.Provider>;
}
export function useCatalog(): CatalogContextValue {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error('useCatalog must be used within CatalogProvider');
  return ctx;
}